/**
 * Object Type Form Hook
 * Handles CRUD operations, editing state, and JSON import
 */

import { useState, useRef, ChangeEvent } from 'react'
import { useObjectTypeStore, type ObjectType, type ObjectTypeKind, type Property } from '../../../../../map-editor-shared/store/objectTypeStore'
import { buildApiProperties, pickStyle } from '../../../../../map-editor-shared/store/objectTypeStore/stylePropertySync'
import { objectTypeApi } from '../../../../../map-editor-shared/api'
import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'

/** 타입명은 영어/숫자/공백/하이픈/언더스코어만 허용 */
const ENGLISH_NAME_PATTERN = /^[A-Za-z0-9 _-]+$/

const DEFAULT_FORM_DATA = {
  name: '',
  kind: 'asset' as ObjectTypeKind,
  fontSize: 12,
  labelWidth: 60,
  labelHeight: 24,
  icon: '',
  color: '#3b82f6',
  strokeColor: '#3b82f6',
  fillColor: '#3b82f6',
  opacity: 0.8,
  strokeWidth: 2,
  iconWidth: 32,
  iconHeight: 32,
  colorMode: 'fill' as 'stroke' | 'fill',
  properties: [] as { key: string; type: Property['type']; required: boolean }[],
}

const DEFAULT_ADD_PROPERTIES: { key: string; type: Property['type']; required: boolean }[] = [
  { key: 'position', type: 'string', required: false },
  { key: 'description', type: 'string', required: false },
  { key: 'points', type: 'array', required: false },
]

export interface FormData {
  name: string
  kind: ObjectTypeKind
  fontSize: number
  labelWidth: number
  labelHeight: number
  icon: string
  color: string
  strokeColor: string
  fillColor: string
  opacity: number
  strokeWidth: number
  iconWidth: number
  iconHeight: number
  colorMode: 'stroke' | 'fill'
  properties: { key: string; type: Property['type']; required: boolean }[]
}

export function useObjectTypeForm() {
  const addType = useObjectTypeStore(state => state.addType)
  const updateType = useObjectTypeStore(state => state.updateType)
  const deleteType = useObjectTypeStore(state => state.deleteType)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    ...DEFAULT_FORM_DATA,
    properties: [...DEFAULT_ADD_PROPERTIES],
  })
  const [showJson, setShowJson] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const jsonInputRef = useRef<HTMLInputElement>(null)

  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM_DATA })
  }

  /** 폼 데이터 → 로컬 ObjectType 페이로드 */
  const buildTypePayload = () => ({
    name: formData.name.trim(),
    kind: formData.kind,
    fontSize: formData.fontSize,
    labelWidth: formData.labelWidth,
    labelHeight: formData.labelHeight,
    icon: formData.icon,
    color: formData.color,
    strokeColor: formData.strokeColor,
    fillColor: formData.fillColor,
    opacity: formData.opacity,
    strokeWidth: formData.strokeWidth,
    iconWidth: formData.iconWidth,
    iconHeight: formData.iconHeight,
    colorMode: formData.colorMode,
    properties: formData.properties,
  })

  /** 폼 데이터 → API 생성/수정 요청 (확장 스타일은 __style 속성에 직렬화) */
  const buildApiRequest = () => {
    const payload = buildTypePayload()
    return {
      name: payload.name,
      icon: payload.icon || undefined,
      color: payload.color || undefined,
      properties: buildApiProperties(payload.properties, pickStyle(payload)) as any,
    }
  }

  const handleAdd = async (resetAsset: () => void) => {
    try {
      setError(null)
      if (!formData.name.trim()) {
        setError('타입명을 입력하세요')
        return
      }
      if (!ENGLISH_NAME_PATTERN.test(formData.name.trim())) {
        setError('타입명은 영어로만 입력하세요 (A-Z, 0-9, 공백, -, _)')
        return
      }

      // API 에 먼저 생성해 서버 발급 id 를 로컬 id 로 사용한다.
      // (loadTypes 가 API 응답으로 로컬 목록을 재구성하므로, API 미동기 타입은
      // 에디터 재진입 시 사라진다.) API 실패 시 로컬 전용으로 fallback.
      let serverId: string | undefined
      const projectId = useObjectTypeStore.getState().currentProjectId
      if (projectId) {
        try {
          const resp = await objectTypeApi.create(projectId, buildApiRequest())
          if (resp.data?.id != null) serverId = String(resp.data.id)
        } catch (apiErr) {
          log.warn(LogCategory.API, 'ObjectType API create failed — keeping local only', {
            error: apiErr instanceof Error ? apiErr.message : apiErr,
          })
        }
      }

      addType({
        ...buildTypePayload(),
        ...(serverId ? { id: serverId } : {}),
      })

      setFormData({ ...DEFAULT_FORM_DATA })
      resetAsset()
      setShowAddForm(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : '타입 추가 실패')
    }
  }

  const handleUpdate = async (id: string, resetAsset: () => void) => {
    try {
      setError(null)
      if (!formData.name.trim()) {
        setError('타입명을 입력하세요')
        return
      }
      if (!ENGLISH_NAME_PATTERN.test(formData.name.trim())) {
        setError('타입명은 영어로만 입력하세요 (A-Z, 0-9, 공백, -, _)')
        return
      }

      updateType(id, buildTypePayload())

      // API 동기화 — 서버 발급(숫자) id 인 타입만. 실패해도 로컬 수정은 유지.
      const projectId = useObjectTypeStore.getState().currentProjectId
      if (projectId && /^\d+$/.test(id)) {
        objectTypeApi.update(projectId, Number(id), buildApiRequest()).catch((apiErr) => {
          log.warn(LogCategory.API, 'ObjectType API update failed — local change kept', {
            id,
            error: apiErr instanceof Error ? apiErr.message : apiErr,
          })
        })
      }

      setEditingId(null)
      setFormData({ ...DEFAULT_FORM_DATA })
      resetAsset()
    } catch (err) {
      setError(err instanceof Error ? err.message : '타입 수정 실패')
    }
  }

  const handleDelete = (id: string) => {
    if (window.confirm('이 객체 타입을 삭제하시겠습니까?')) {
      deleteType(id)

      // API 동기화 — 서버 발급(숫자) id 인 타입만 소프트 삭제.
      const projectId = useObjectTypeStore.getState().currentProjectId
      if (projectId && /^\d+$/.test(id)) {
        objectTypeApi.delete(projectId, Number(id)).catch((apiErr) => {
          log.warn(LogCategory.API, 'ObjectType API delete failed', {
            id,
            error: apiErr instanceof Error ? apiErr.message : apiErr,
          })
        })
      }
    }
  }

  const startEdit = (type: ObjectType, initPreview: (icon: string | undefined) => void) => {
    setEditingId(type.id)
    setFormData({
      name: type.name,
      kind: type.kind || 'asset',
      fontSize: type.fontSize ?? 12,
      labelWidth: type.labelWidth ?? 60,
      labelHeight: type.labelHeight ?? 24,
      icon: type.icon || '',
      color: type.color || '#3b82f6',
      strokeColor: type.strokeColor || type.color || '#3b82f6',
      fillColor: type.fillColor || (type.colorMode === 'stroke' ? 'none' : type.color || '#3b82f6'),
      opacity: type.opacity ?? 0.8,
      strokeWidth: type.strokeWidth ?? 2,
      iconWidth: type.iconWidth || 32,
      iconHeight: type.iconHeight || 32,
      colorMode: type.colorMode || 'fill',
      properties: [...type.properties],
    })
    initPreview(type.icon)
    setShowAddForm(false)
    setError(null)
  }

  const cancelEdit = (resetAsset: () => void) => {
    setEditingId(null)
    setShowAddForm(false)
    setFormData({ ...DEFAULT_FORM_DATA })
    resetAsset()
    setError(null)
  }

  const handleShowAddForm = () => {
    setFormData({
      ...DEFAULT_FORM_DATA,
      properties: [...DEFAULT_ADD_PROPERTIES],
    })
    setShowAddForm(true)
    setError(null)
  }

  const handlePriorityChange = (typeId: string, priority: number) => {
    updateType(typeId, { priority })
  }

  // --- JSON Import ---

  const ensureDefaultProperties = (properties: any[]): any[] => {
    const defaultProps = [
      { key: 'name', type: 'string', required: true },
      { key: 'position', type: 'string', required: true },
      { key: 'points', type: 'array', required: true },
      { key: 'description', type: 'string', required: false }
    ]

    const existingKeys = new Set(properties.map(p => p.key))
    const missingProps = defaultProps.filter(p => !existingKeys.has(p.key))

    return [...properties, ...missingProps]
  }

  const convertValueToPropertyType = (value: any): 'string' | 'number' | 'boolean' | 'array' => {
    if (Array.isArray(value)) return 'array'
    if (typeof value === 'number') return 'number'
    if (typeof value === 'boolean') return 'boolean'
    return 'string'
  }

  const handleJsonUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string)
        let addedCount = 0
        const errors: string[] = []

        // Check if it's an object with keys (new format)
        if (typeof json === 'object' && json !== null && !Array.isArray(json)) {
          Object.entries(json).forEach(([key, value]: [string, any]) => {
            try {
              if (value === null || value === undefined) {
                errors.push(`"${key}": 값이 null 또는 undefined입니다`)
                return
              }

              const name = value.name || key

              let properties: any[] = []

              if (Array.isArray(value.properties)) {
                properties = value.properties
              } else if (typeof value === 'object' && !Array.isArray(value)) {
                properties = Object.entries(value)
                  .filter(([fieldKey]) => fieldKey !== 'name' && fieldKey !== 'icon' && fieldKey !== 'color')
                  .map(([fieldKey, fieldValue]) => ({
                    key: fieldKey,
                    type: convertValueToPropertyType(fieldValue),
                    required: false
                  }))
              }

              const completeProperties = ensureDefaultProperties(properties)

              addType({
                name: name,
                icon: value.icon || '',
                color: value.color || '#3b82f6',
                properties: completeProperties
              })
              addedCount++
            } catch (err) {
              errors.push(`"${key}": ${err instanceof Error ? err.message : 'Unknown error'}`)
            }
          })
        }
        // Support legacy array format
        else if (Array.isArray(json)) {
          json.forEach((item, index) => {
            try {
              if (item === null || item === undefined) {
                errors.push(`Object ${index + 1}: 값이 null 또는 undefined입니다`)
                return
              }

              const name = item.name || `Object_${index + 1}`

              let properties: any[] = []

              if (Array.isArray(item.properties)) {
                properties = item.properties
              } else if (typeof item === 'object' && !Array.isArray(item)) {
                properties = Object.entries(item)
                  .filter(([fieldKey]) => fieldKey !== 'name' && fieldKey !== 'icon' && fieldKey !== 'color')
                  .map(([fieldKey, fieldValue]) => ({
                    key: fieldKey,
                    type: convertValueToPropertyType(fieldValue),
                    required: false
                  }))
              }

              const completeProperties = ensureDefaultProperties(properties)

              addType({
                name: name,
                icon: item.icon || '',
                color: item.color || '#3b82f6',
                properties: completeProperties
              })
              addedCount++
            } catch (err) {
              errors.push(`Object ${index + 1}: ${err instanceof Error ? err.message : 'Unknown error'}`)
            }
          })
        } else {
          setError('Invalid JSON format: Expected an object or array')
          if (jsonInputRef.current) jsonInputRef.current.value = ''
          return
        }

        // Show results
        if (errors.length > 0) {
          const errorMsg = `${addedCount}개 생성됨, ${errors.length}개 실패:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...외 ${errors.length - 5}개` : ''}`
          setError(errorMsg)
        } else if (addedCount > 0) {
          setSuccessMessage(`\u2705 ${addedCount}개의 객체 타입이 생성되었습니다`)
          setTimeout(() => setSuccessMessage(null), 3000)
        } else {
          setError('유효한 객체 타입을 찾을 수 없습니다')
        }
      } catch (err) {
        setError('Failed to parse JSON: ' + (err instanceof Error ? err.message : 'Unknown error'))
      }
      if (jsonInputRef.current) jsonInputRef.current.value = ''
    }
    reader.readAsText(file)
  }

  return {
    editingId,
    showAddForm,
    formData,
    setFormData,
    showJson,
    setShowJson,
    error,
    setError,
    successMessage,
    jsonInputRef,
    handleAdd,
    handleUpdate,
    handleDelete,
    startEdit,
    cancelEdit,
    handleShowAddForm,
    handlePriorityChange,
    handleJsonUpload,
    resetForm,
  }
}
